import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Typography, { typography } from '@repo/ui/typography'
import { useFormContext } from 'react-hook-form'

import {
  MedicalConditionEnum,
  medicalConditionEnumMapping
} from '../../../../lib/types/enums/enrollment/medical-condition-enum'
import { EnrollmentInput } from '../../../../lib/types/models/enrollment/enrollment-input'
import { getSelectedEnumOption } from '../../../../lib/utils/functions/misc.functions'
import { FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ControlledSelectInput from '../../../elements/inputs/controlled-select-input'
import EnrollmentAdditionalMedicalInformationFields from './enrollment-additional-medical-information-fields'

type Props = {
  // the backend decides whether LEC may still write the main health field
  canEditMainMedicalInformations: boolean
}

const EnrollmentHealthInformationTranslation = ({ canEditMainMedicalInformations }: Props) => {
  const form = useFormContext<EnrollmentInput>()

  const [medicalLevel, medicalInformations, medicalInformationsEn, hasChildHealtInformation] =
    form.watch([
      'medicalLevel',
      'medicalInformations',
      'medicalInformationsEn',
      'hasChildHealtInformation'
    ])

  const hasMedicalInformation = Boolean(medicalInformations?.trim())

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-2 items-center">
        <Typography variant="label-medium">Niveau médical</Typography>

        <FormField
          control={form.control}
          name="medicalLevel"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <ControlledSelectInput
                  {...field}
                  mode="badge"
                  variantColor={
                    medicalLevel ? medicalConditionEnumMapping[medicalLevel]?.variant : undefined
                  }
                  // RAS clears the main health field, which the backend refuses when it is locked
                  options={Object.values(MedicalConditionEnum)
                    .filter(
                      (value) =>
                        canEditMainMedicalInformations || value !== MedicalConditionEnum.RAS
                    )
                    .map((value) => ({
                      label: medicalConditionEnumMapping[value].label,
                      value
                    }))}
                  label={
                    medicalLevel ? medicalConditionEnumMapping[medicalLevel]?.label : undefined
                  }
                  disabled={!hasChildHealtInformation}
                  selectedOption={getSelectedEnumOption(field.value, medicalConditionEnumMapping)}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <div className="flex flex-col gap-3">
        <Typography variant="label-medium">Informations médicales</Typography>
        {canEditMainMedicalInformations ? (
          <FormField
            control={form.control}
            name="medicalInformations"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledInput {...field} type="textarea" label="Information sur la santé" />
                </FormControl>
              </FormItem>
            )}
          />
        ) : (
          <Typography variant="tag">{medicalInformations}</Typography>
        )}

        {medicalInformationsEn !== undefined && (
          <FormField
            control={form.control}
            name="medicalInformationsEn"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledInput
                    {...field}
                    type="textarea"
                    placeholder="Informations médicales"
                    style={{
                      ...typography['small-medium'],
                      color: tailwindColors.grey.light.active
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        )}
      </div>
      {hasMedicalInformation && <EnrollmentAdditionalMedicalInformationFields />}
    </div>
  )
}

export default EnrollmentHealthInformationTranslation
